import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  Box,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  addButton: {
    backgroundColor: "#ff7900",
    color: "white",
    "&:hover": {
      backgroundColor: "#e06600",
    },
  },
  tableContainer: {
    maxHeight: 600,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  dialogContent: {
    minWidth: 500,
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  productOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  productImage: {
    width: 60,
    height: 60,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid #e0e0e0",
  },
  productDetails: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  productName: {
    fontWeight: 500,
    fontSize: "0.9rem",
    marginBottom: 2,
  },
  productMeta: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  productPrice: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: "0.9rem",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  limitedOfferBadge: {
    backgroundColor: "#d32f2f",
    color: "white",
    fontWeight: "bold",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: "0.75rem",
  },
});

const hebrewLabels = {
  title: "ניהול מבצעים לזמן מוגבל",
  subtitle: "כאן תוכל להוסיף תגיות 'מבצע לזמן מוגבל' למוצרים ספציפיים",
  buttons: {
    add: "הוסף מבצע חדש",
    delete: "מחק",
    save: "שמור",
    cancel: "ביטול",
  },
  table: {
    ean: "EAN",
    product: "מוצר",
    brand: "יצרן",
    size: "מידה",
    price: "מחיר",
    status: "סטטוס",
    expiresAt: "תאריך תפוגה",
    actions: "פעולות",
    createdAt: "נוסף בתאריך",
  },
  dialog: {
    create: "הוסף מבצע לזמן מוגבל",
    delete: "מחיקת מבצע",
    deleteConfirm: "האם אתה בטוח שברצונך למחוק מבצע זה?",
    selectProduct: "בחר מוצר",
    selectProductHelp: "חפש לפי שם מוצר, EAN, או יצרן",
    setExpiration: "הגדר תאריך תפוגה",
    expirationDate: "תאריך ושעת תפוגה",
    expirationHelp: "המבצע יפסיק להיות פעיל בזמן זה (שעון ישראל)",
  },
  messages: {
    loading: "טוען...",
    loadingProducts: "טוען מוצרים...",
    noOffers: "אין מבצעים פעילים כרגע",
    addSuccess: "המבצע נוסף בהצלחה!",
    deleteSuccess: "המבצע נמחק בהצלחה!",
    error: "אירעה שגיאה",
    noProductsFound: "לא נמצאו מוצרים",
  },
  status: {
    active: "פעיל",
  },
};

const basePath = utils.getEdgeBasePath();

class LimitedTimeOffersManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      offers: [],
      products: [],
      loading: true,
      loadingProducts: false,
      dialogOpen: false,
      selectedProduct: null,
      hasExpiration: false,
      expirationDate: "",
      deleteDialogOpen: false,
      offerToDelete: null,
      snackbar: {
        open: false,
        message: "",
        severity: "success",
      },
    };
  }

  componentDidMount() {
    this.loadOffers();
    this.loadProducts();
  }

  loadOffers = async () => {
    try {
      this.setState({ loading: true });
      const response = await fetch(`${basePath}/api/v1/admin/limited-time-offers`);
      const data = await response.json();

      if (data.success) {
        this.setState({ offers: data.offers });
      } else {
        this.showSnackbar(hebrewLabels.messages.error, "error");
      }
    } catch (error) {
      console.error("Error loading offers:", error);
      this.showSnackbar(hebrewLabels.messages.error, "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  loadProducts = async () => {
    try {
      this.setState({ loadingProducts: true });
      const response = await fetch(`${basePath}/api/v1/admin/products/list`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await response.json();

      if (data.ok && data.products) {
        this.setState({ products: data.products });
      } else {
        console.error("Failed to load products:", data);
        this.showSnackbar("שגיאה בטעינת המוצרים", "error");
      }
    } catch (error) {
      console.error("Error loading products:", error);
      this.showSnackbar("שגיאה בטעינת המוצרים", "error");
    } finally {
      this.setState({ loadingProducts: false });
    }
  };

  openDialog = () => {
    // Default to tomorrow at 23:59 Israel time
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 0, 0);
    const israelTime = tomorrow.toISOString().slice(0, 16);
    
    this.setState({
      dialogOpen: true,
      selectedProduct: null,
      hasExpiration: false,
      expirationDate: israelTime,
    });
  };

  closeDialog = () => {
    this.setState({
      dialogOpen: false,
      selectedProduct: null,
    });
  };

  handleProductChange = (event, value) => {
    this.setState({ selectedProduct: value });
  };

  handleExpirationToggle = (event) => {
    this.setState({ hasExpiration: event.target.checked });
  };

  handleExpirationDateChange = (event) => {
    this.setState({ expirationDate: event.target.value });
  };

  handleSave = async () => {
    const { selectedProduct, hasExpiration, expirationDate } = this.state;

    if (!selectedProduct) {
      this.showSnackbar("נא לבחור מוצר", "error");
      return;
    }

    if (hasExpiration && !expirationDate) {
      this.showSnackbar("נא לבחור תאריך ושעת תפוגה", "error");
      return;
    }

    try {
      const body = {
        ean: selectedProduct.ean,
      };

      if (hasExpiration) {
        // Convert to ISO string for backend
        body.expiresAt = new Date(expirationDate).toISOString();
      }

      const response = await fetch(`${basePath}/api/v1/admin/limited-time-offers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        this.showSnackbar(hebrewLabels.messages.addSuccess, "success");
        this.closeDialog();
        this.loadOffers();
      } else {
        this.showSnackbar(data.error || hebrewLabels.messages.error, "error");
      }
    } catch (error) {
      console.error("Error creating offer:", error);
      this.showSnackbar(hebrewLabels.messages.error, "error");
    }
  };

  openDeleteDialog = (offer) => {
    this.setState({
      deleteDialogOpen: true,
      offerToDelete: offer,
    });
  };

  closeDeleteDialog = () => {
    this.setState({
      deleteDialogOpen: false,
      offerToDelete: null,
    });
  };

  handleDelete = async () => {
    const { offerToDelete } = this.state;

    if (!offerToDelete) return;

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/limited-time-offers/${offerToDelete._id}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        this.showSnackbar(hebrewLabels.messages.deleteSuccess, "success");
        this.closeDeleteDialog();
        this.loadOffers();
      } else {
        this.showSnackbar(data.error || hebrewLabels.messages.error, "error");
      }
    } catch (error) {
      console.error("Error deleting offer:", error);
      this.showSnackbar(hebrewLabels.messages.error, "error");
    }
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  closeSnackbar = () => {
    this.setState({
      snackbar: { ...this.state.snackbar, open: false },
    });
  };

  formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("he-IL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Jerusalem",
    });
  };

  formatExpirationDate = (expiresAt) => {
    if (!expiresAt) return "ללא תפוגה";
    const expDate = new Date(expiresAt);
    const now = new Date();
    
    // Check if expired
    if (expDate < now) {
      return "פג תוקף";
    }
    
    return new Date(expiresAt).toLocaleDateString("he-IL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Jerusalem",
    });
  };

  getProductImageUrl = (product) => {
    if (!product || !product.cdn || !product.cdn.photos || product.cdn.photos.length === 0) {
      return "";
    }
    return `https://d38yyiqzvge5j4.cloudfront.net/${product.cdn.photos[0]}`;
  };

  formatProductLabel = (product) => {
    if (!product) return "";
    const parts = [];
    if (product.brand) parts.push(product.brand);
    if (product.displayName) parts.push(product.displayName);
    if (product.displaySize) parts.push(product.displaySize);
    if (product.ean) parts.push(`(${product.ean})`);
    return parts.join(" - ");
  };

  render() {
    const { classes } = this.props;
    const {
      offers,
      products,
      loading,
      loadingProducts,
      dialogOpen,
      selectedProduct,
      hasExpiration,
      expirationDate,
      deleteDialogOpen,
      offerToDelete,
      snackbar,
    } = this.state;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography style={{ marginLeft: 16 }}>
            {hebrewLabels.messages.loading}
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.title}>
            <LocalOfferIcon style={{ fontSize: 32, color: "#ff7900" }} />
            <div>
              <Typography variant="h4" component="h1">
                {hebrewLabels.title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {hebrewLabels.subtitle}
              </Typography>
            </div>
          </div>
          <Button
            variant="contained"
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={this.openDialog}
          >
            {hebrewLabels.buttons.add}
          </Button>
        </div>

        {offers.length === 0 ? (
          <div className={classes.emptyState}>
            <LocalOfferIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
            <Typography variant="h6">{hebrewLabels.messages.noOffers}</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              לחץ על '{hebrewLabels.buttons.add}' כדי להוסיף מבצע חדש
            </Typography>
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{hebrewLabels.table.ean}</TableCell>
                  <TableCell>{hebrewLabels.table.product}</TableCell>
                  <TableCell>{hebrewLabels.table.brand}</TableCell>
                  <TableCell>{hebrewLabels.table.size}</TableCell>
                  <TableCell>{hebrewLabels.table.price}</TableCell>
                  <TableCell>{hebrewLabels.table.status}</TableCell>
                  <TableCell>{hebrewLabels.table.expiresAt}</TableCell>
                  <TableCell>{hebrewLabels.table.createdAt}</TableCell>
                  <TableCell align="center">{hebrewLabels.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.map((offer) => (
                  <TableRow key={offer._id}>
                    <TableCell>{offer.product?.ean || offer.ean}</TableCell>
                    <TableCell>{offer.product?.displayName || "-"}</TableCell>
                    <TableCell>{offer.product?.brand || "-"}</TableCell>
                    <TableCell>{offer.product?.displaySize || "-"}</TableCell>
                    <TableCell>
                      {offer.product?.price && !isNaN(Number(offer.product.price))
                        ? `${Number(offer.product.price).toFixed(2)} ₪`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {offer.active ? (
                        <Chip
                          label={hebrewLabels.status.active}
                          className={classes.activeChip}
                          size="small"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>{this.formatExpirationDate(offer.expiresAt)}</TableCell>
                    <TableCell>{this.formatDate(offer.createdAt)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => this.openDeleteDialog(offer)}
                        size="small"
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Dialog */}
        <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>{hebrewLabels.dialog.create}</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Autocomplete
              options={products}
              getOptionLabel={this.formatProductLabel}
              value={selectedProduct}
              onChange={this.handleProductChange}
              loading={loadingProducts}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                
                // Split search terms by space to allow multi-term search
                const searchTerms = inputValue.toLowerCase().trim().split(/\s+/);
                
                return options.filter((option) => {
                  // Combine all searchable fields into one string
                  const searchableText = [
                    option.brand || "",
                    option.displayName || "",
                    option.displaySize || "",
                    option.ean || "",
                    option.name || ""
                  ].join(" ").toLowerCase();
                  
                  // Check if ALL search terms exist in the combined text
                  return searchTerms.every(term => searchableText.includes(term));
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={hebrewLabels.dialog.selectProduct}
                  variant="outlined"
                  helperText={hebrewLabels.dialog.selectProductHelp}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingProducts ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderOption={(option) => (
                <div className={classes.productOption}>
                  {this.getProductImageUrl(option) && (
                    <img
                      src={this.getProductImageUrl(option)}
                      alt={option.displayName}
                      className={classes.productImage}
                    />
                  )}
                  <div className={classes.productDetails}>
                    <div className={classes.productName}>
                      {option.brand} {option.displayName}
                    </div>
                    <div className={classes.productMeta}>
                      {option.displaySize} • EAN: {option.ean}
                    </div>
                    {option.price && !isNaN(Number(option.price)) && (
                      <div className={classes.productPrice}>
                        {Number(option.price).toFixed(2)} ₪
                      </div>
                    )}
                  </div>
                </div>
              )}
            />

            <Box mt={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasExpiration}
                    onChange={this.handleExpirationToggle}
                    color="primary"
                  />
                }
                label={hebrewLabels.dialog.setExpiration}
              />
            </Box>

            {hasExpiration && (
              <Box mt={2}>
                <TextField
                  label={hebrewLabels.dialog.expirationDate}
                  type="datetime-local"
                  value={expirationDate}
                  onChange={this.handleExpirationDateChange}
                  fullWidth
                  variant="outlined"
                  helperText={hebrewLabels.dialog.expirationHelp}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog}>{hebrewLabels.buttons.cancel}</Button>
            <Button
              onClick={this.handleSave}
              color="primary"
              variant="contained"
              disabled={!selectedProduct}
            >
              {hebrewLabels.buttons.save}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={this.closeDeleteDialog}>
          <DialogTitle>{hebrewLabels.dialog.delete}</DialogTitle>
          <DialogContent>
            <Typography>{hebrewLabels.dialog.deleteConfirm}</Typography>
            {offerToDelete && offerToDelete.product && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  <strong>מוצר:</strong> {offerToDelete.product.brand}{" "}
                  {offerToDelete.product.displayName} {offerToDelete.product.displaySize}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDeleteDialog}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button onClick={this.handleDelete} color="secondary" variant="contained">
              {hebrewLabels.buttons.delete}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

LimitedTimeOffersManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(LimitedTimeOffersManager);

